import fuzzysort from 'fuzzysort'
import { computed, makeObservable } from 'mobx'

import { map } from '@src/lib/collections'
import isTruthy from '@src/lib/isTruthy'
import { formatted } from '@src/lib/phone-number'
import { stripSpecials } from '@src/lib/string'
import type { Identity, MemberModel } from '@src/service/model'
import type {
  CodableContact,
  ContactModel,
} from '@src/service/model/contact/ContactModel'
import type { IntegrationContactModel } from '@src/service/model/contact/IntegrationContactModel'
import type { ContactRepository } from '@src/service/worker/repository/contact'
import type { IntegrationContactRepository } from '@src/service/worker/repository/integration-contact'
import type { MemberRepository } from '@src/service/worker/repository/member'

import { BaseService } from './base'

type IdentitySearchResult =
  | { t: 'contact'; m: string; i: CodableContact }
  | { t: 'integrationContact'; m: string; i: IntegrationContactModel }
  | { t: 'member'; m: string; i: MemberModel }

type InternalContact = { t: 'contact'; n: Fuzzysort.Prepared; i: CodableContact['id'] }
type InternalIntegrationContact = {
  t: 'integrationContact'
  n: Fuzzysort.Prepared
  i: IntegrationContactModel['id']
}
type InternalMember = { t: 'member'; n: Fuzzysort.Prepared; i: MemberModel['id'] }
type InternalIdentity = InternalContact | InternalIntegrationContact | InternalMember

export class SearchService extends BaseService {
  private _contacts = new Map<string, InternalContact>()
  private _integrationContacts = new Map<string, InternalIntegrationContact>()
  private _members = new Map<string, InternalMember>()
  private _companies = new Map<string, boolean>()

  constructor(
    private contactRepo: ContactRepository,
    private memberRepo: MemberRepository,
    private integrationContactRepo: IntegrationContactRepository,
  ) {
    super()

    contactRepo.observe((ev) => {
      if (ev.type === 'put') {
        ev.objects.forEach(this.indexContact)
      } else if (ev.type === 'delete') {
        ev.objects.forEach(this.removeIdentity)
      }
    })
    memberRepo.observe((ev) => {
      if (ev.type === 'put') {
        ev.objects.forEach(this.indexMember)
      } else if (ev.type === 'delete') {
        ev.objects.forEach(this.removeIdentity)
      }
    })
    integrationContactRepo.observe((ev) => {
      if (ev.type === 'put') {
        ev.objects.forEach(this.indexIntegrationContact)
      } else if (ev.type === 'delete') {
        ev.objects.forEach(this.removeIdentity)
      }
    })

    makeObservable(this, {
      _identities: computed,
    })
  }

  get _identities(): InternalIdentity[] {
    return [
      ...Array.from(this._members.values()),
      ...Array.from(this._contacts.values()),
      ...Array.from(this._integrationContacts.values()),
    ]
  }

  hydrate() {
    return Promise.all([
      this.contactRepo.all().then((contacts) => {
        contacts.forEach(this.indexContact)
      }),
      this.memberRepo.all().then((members) => {
        members.forEach(this.indexMember)
      }),
      this.integrationContactRepo.all().then((integrationContact) => {
        integrationContact.forEach(this.indexIntegrationContact)
      }),
    ])
  }

  /**
   * Fuzzy search across members, contacts and groups
   */
  async identities(
    query: string,
    limit = 20,
    type: 'all' | 'members' | 'contacts' = 'all',
    areIntegrationContactsEnabled: boolean = false,
  ): Promise<IdentitySearchResult[]> {
    query = this.prepare(query)

    const identities =
      (() => {
        if (type === 'all') {
          return this._identities
        }
        if (type === 'contacts') {
          return [...this._contacts.values(), ...this._integrationContacts.values()]
        }
        if (type === 'members') {
          return Array.from(this._members.values())
        }
      })() ?? []

    const result = fuzzysort.go<InternalIdentity>(query, identities, {
      limit,
      keys: ['n', 'phone0', 'phone1', 'phone2', 'phone3', 'phone4', 'phone5', 'phone6'],
      allowTypo: false,
      threshold: -10000,
    })

    const objectIds = result.reduce(
      (final, r) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- FIXME: Fix this ESLint violation!
        r.obj.t === 'contact'
          ? final.contacts.push(r.obj.i)
          : r.obj.t === 'integrationContact'
          ? final.integrationContacts.push(r.obj.i)
          : final.members.push(r.obj.i)
        return final
      },
      {
        contacts: [] as string[],
        integrationContacts: [] as string[],
        members: [] as string[],
      },
    )

    const allContactsById = await this.contactRepo.getBulk(objectIds.contacts)
    const allIntegrationContactsById = await this.integrationContactRepo.getBulk(
      objectIds.integrationContacts,
    )
    const allMembersById = await this.memberRepo.getBulk(objectIds.members)

    const contactsById = map(allContactsById.filter(isTruthy), 'id')
    const integrationContactsById = map(allIntegrationContactsById.filter(isTruthy), 'id')
    const membersById = map(allMembersById.filter(isTruthy), 'id')

    return result
      .map((r): IdentitySearchResult | null => {
        const type = r.obj.t
        const match = r.slice(1).find((a) => a?.target)?.target ?? ''
        const id = r.obj.i
        let identity: Identity | null = null

        if (type === 'contact') {
          identity = contactsById[id] ?? null
        }

        if (type === 'integrationContact') {
          if (!areIntegrationContactsEnabled) {
            return null
          }

          identity = integrationContactsById[id] ?? null
        }

        if (type === 'member') {
          identity = membersById[id] ?? null
        }

        if (!identity) {
          return null
        }

        switch (type) {
          case 'contact': {
            return {
              m: match,
              t: type,
              i: identity as ContactModel,
            }
          }
          case 'integrationContact': {
            return {
              m: match,
              t: type,
              i: identity as IntegrationContactModel,
            }
          }
          case 'member': {
            return {
              m: match,
              t: type,
              i: identity as MemberModel,
            }
          }
        }
      })
      .filter(isTruthy)
  }

  /**
   * Fuzzy search across all companies
   */
  async companies(query: string, limit = 10): Promise<string[]> {
    query = this.prepare(query)
    const result = await fuzzysort.goAsync(query, Object.keys(this._companies), {
      limit,
      allowTypo: false,
      threshold: -10000,
    })
    return result.map((r) => r.target)
  }

  private prepare(query: string): string {
    const stripped = stripSpecials(query)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
    if (isNaN(stripped as any)) {
      return query
    } else {
      return stripped
    }
  }

  private indexContact = (contact: CodableContact) => {
    if (contact.company) {
      this._companies[contact.company] = true
    }

    const name = [contact.firstName, contact.lastName, contact.company]
      .filter((a) => a)
      .join(' ')

    this._contacts.set(contact.id, {
      i: contact.id,
      t: 'contact',
      n: fuzzysort.prepare(name),
      ...contact?.items
        ?.filter((t) => t.type === 'phone-number')
        .reduce((final, item, index) => {
          final[`phone${index}`] = fuzzysort.prepare(formatted(item.value as string))
          return final
        }, {}),
    })
  }

  private indexIntegrationContact = (contact: IntegrationContactModel) => {
    const name = [contact.firstName, contact.lastName, contact.company]
      .filter((a) => a)
      .join(' ')

    this._integrationContacts.set(contact.id, {
      i: contact.id,
      t: 'integrationContact',
      n: fuzzysort.prepare(name),
      ...contact?.phoneNumbers?.reduce((final, item, index) => {
        final[`phone${index}`] = fuzzysort.prepare(formatted(item))
        return final
      }, {}),
    })
  }

  private indexMember = (member: MemberModel) => {
    this._members.set(member.id, {
      i: member.id,
      t: 'member',
      n: fuzzysort.prepare(member.firstName + ' ' + member.lastName + ' ' + member.email),
      ...member.phoneNumbers.reduce((final, item, index) => {
        final[`phone${index}`] = fuzzysort.prepare(formatted(item.number))
        return final
      }, {}),
    })
  }

  private removeIdentity = (identity: Identity) => {
    if (!identity) {
      return
    }
    this._contacts.delete(identity.id)
    this._members.delete(identity.id)
    this._integrationContacts.delete(identity.id)
  }
}
